<template>
	<Dialog v-model:visible="showModal" modal :closable="false" class="w-full md:w-4/5 xl:w-3/4 2xl:w-2/3"
		:closeOnEscape="false">
		<LoadingStep v-if="loading" />
		<template v-else>
			<!-- Progress Indicator - Subtle dots instead of stepper -->
			<div class="flex justify-center mb-6">
				<div class="flex space-x-2">
					<div v-for="step in totalSteps" :key="step" :class="[
						'w-2 h-2 rounded-full transition-colors duration-200',
						step <= currentStep ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
					]"></div>
				</div>
			</div>

			<!-- Welcome Card -->
			<div v-if="currentStep === 0" class="text-center space-y-6">
				<div class="space-y-4">
					<div
						class="w-16 h-16 mx-auto bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
						<i class="pi pi-home text-blue-600 dark:text-blue-400 text-2xl"></i>
					</div>
					<h2 class="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{{ $t('onboarding.welcome.title') }}
					</h2>
					<p class="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
						{{ $t('onboarding.welcome.description') }}
					</p>
				</div>

				<div class="grid md:grid-cols-2 gap-6 mt-8 text-left">
					<div
						class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl">
						<div class="flex items-center space-x-3 mb-3">
							<div class="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
								<i class="pi pi-check-square text-white text-sm"></i>
							</div>
							<h3 class="font-semibold text-gray-900 dark:text-gray-100">{{
								$t('onboarding.preview.chores.title') }}</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-400 text-sm">{{
							$t('onboarding.preview.chores.description') }}</p>
					</div>

					<div
						class="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6 rounded-xl">
						<div class="flex items-center space-x-3 mb-3">
							<div class="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
								<i class="pi pi-gift text-white text-sm"></i>
							</div>
							<h3 class="font-semibold text-gray-900 dark:text-gray-100">{{
								$t('onboarding.preview.prizes.title') }}</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-400 text-sm">{{
							$t('onboarding.preview.prizes.description') }}</p>
					</div>
				</div>

				<Button @click="currentStep = 1" size="large" class="mt-8">
					{{ $t('onboarding.buttons.letsStart') }}
					<i class="pi pi-arrow-right ml-2"></i>
				</Button>
			</div>

			<!-- Chores Step -->
			<AddChoresStep v-else-if="currentStep === 1" @next="handleChoresNext" @skip="currentStep = 2" />

			<!-- Prizes Step -->
			<SetPrizeStep v-else-if="currentStep === 2" @next="handlePrizesNext" @back="currentStep = 1" />

			<!-- Completion Step -->
			<div v-else-if="currentStep === 3" class="text-center space-y-6">
				<div class="space-y-4">
					<div
						class="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
						<i class="pi pi-check text-green-600 dark:text-green-400 text-2xl"></i>
					</div>
					<h2 class="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{{ $t('onboarding.completion.title') }}
					</h2>
					<p class="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
						{{ $t('onboarding.completion.description') }}
					</p>
				</div>

				<div class="grid md:grid-cols-3 gap-4 mt-8 text-left">
					<div class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
						<h4 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">{{
							$t('onboarding.completion.nextSteps.track') }}</h4>
						<p class="text-sm text-gray-600 dark:text-gray-400">{{
							$t('onboarding.completion.nextSteps.trackDesc') }}</p>
					</div>
					<div class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
						<h4 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">{{
							$t('onboarding.completion.nextSteps.compete') }}</h4>
						<p class="text-sm text-gray-600 dark:text-gray-400">{{
							$t('onboarding.completion.nextSteps.competeDesc') }}</p>
					</div>
					<div class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
						<h4 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">{{
							$t('onboarding.completion.nextSteps.win') }}</h4>
						<p class="text-sm text-gray-600 dark:text-gray-400">{{
							$t('onboarding.completion.nextSteps.winDesc') }}</p>
					</div>
				</div>

				<Button @click="completeOnboarding()" size="large" class="mt-8">
					{{ $t('onboarding.buttons.startUsing') }}
					<i class="pi pi-home ml-2"></i>
				</Button>
			</div>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useOnboardingStore, usePrizeSettingsStore, useChoreSettingsStore } from '@/stores';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import AddChoresStep from './AddChoresStep.vue';
import SetPrizeStep from './SetPrizeStep.vue';
import { storeToRefs } from "pinia";
import LoadingStep from './LoadingStep.vue';
import type { CreateInitialChoreDto } from "@/client";

const onboardingStore = useOnboardingStore();
const { ensurePrizeSettingsDataLoaded } = usePrizeSettingsStore();
const { ensureChoreSettingsDataLoaded } = useChoreSettingsStore();
const { needsOnboarding } = storeToRefs(onboardingStore);
const showModal = ref(false);
const currentStep = ref(0);
const totalSteps = 4; // Welcome, Chores, Prizes, Completion

watch(() => needsOnboarding.value, (newValue) => {
	console.log("Onboarding state changed:", newValue);
	showModal.value = needsOnboarding.value
}, { immediate: true });

const loading = ref(true);
onMounted(async () => {
	await Promise.all([
		ensurePrizeSettingsDataLoaded(),
		ensureChoreSettingsDataLoaded()
	]);
	loading.value = false;
});

const initialChores = ref<CreateInitialChoreDto[]>([]);
const initialSuggestions = ref<string[]>([]);

const handleChoresNext = async (chores: CreateInitialChoreDto[]) => {
	initialChores.value = chores;
	currentStep.value = 2;
};

const handlePrizesNext = async (suggestions: string[]) => {
	initialSuggestions.value = suggestions;
	currentStep.value = 3;
};

const completeOnboarding = async () => {
	await onboardingStore.submitHouseholdOnboarding({
		chores: initialChores.value,
		prizeSuggestions: initialSuggestions.value,
	});
	showModal.value = false;
};
</script>